import React, { useEffect, useState } from 'react';
import Styled from './AdminBiblioNew.styled';
import { GradientButton, OutlineButton } from 'components/UI/Buttons';
import AdminImageUploader from '../imageUploader/AdminImageUploader';
import { RadioGroup, Radio } from 'react-radio-input';
import { isFileNotNull, isSaveRascunho, isStringsValid } from './isValidNewContentInput';
import AdminBiblioService from 'core/http/service/admin/AdminBiblio.service';
import AdminBiblioItemSave from 'models/admin/AdminBiblioItemSave';
import submitAttach, { AdminBiblioAnexoType, fecthAttachImage, fetchTagsItemId } from './submitAttach';
import { IAdminBiblioEditorContent } from 'pages/admin/subpages/biblio/post/AdminBiblioNewContent';

import checkIcon from '../../../../images/admin/ok.svg';
import AdminTagSelector from '../tagSelector/AdminTagSelector';
import { StringHelp } from '../../../../core/util/StringHelp';
import { IsMobileMaxWidth } from 'core/util/WindowSize';
import { ToastError } from 'components/Toaster';
import ItemBibliotecaRelatedContentListagem from '../itemBibliotecaRelatedContentList/ItemBibliotecaRelatedContentListagem';
import ReactQuill from 'react-quill';
import editorConfig from 'pages/diarioBordo/components/textEditor/editorConfig';
import { useSubmitProgress } from './useSubmitProgress';

export interface IAdminBiblioNewVideo {
    onSaved: () => void;
}

const isMobile = () => IsMobileMaxWidth();

const AdminBiblioNewVideo = ({ onSaved, itemData, categoriaId, categorias }: IAdminBiblioEditorContent) => {
    const [id, setId] = useState<number>(-1);
    const [slug, setSlug] = useState<string>('');
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [isPublic, setIsPublic] = useState('false');
    const [titleTag, setTitleTag] = useState('');
    const [metaDescriptionTag, setMetaDescriptionTag] = useState('');

    const [link, setLink] = useState<string>('');

    const [imageCover, setImageCover] = useState<File>();
    const [preimageCover, setPreImageCover] = useState<string | null>(null);

    const progress = useSubmitProgress();

    const isValidInputs = (): boolean => {
        if (!preimageCover && !imageCover) return false;
        return isStringsValid([title, content, link, slug]);
    };

    const validLink = (value: string): boolean => {
        const re = /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/g;

        return re.test(value.trim());
    };

    const categoriaExistente = (): boolean => {
        return categorias.filter(c => c.ativo).some(c => c.id == categoriaId);
    };

    const submit = async (_status: 'PUBLICADO' | 'RASCUNHO') => {
        if (!validLink(link)) {
            ToastError('Link adicionado inválido ou não corresponde ao um link do Youtube');
            return true;
        }

        progress.setCreating();

        try {
            const service = new AdminBiblioService();

            const itemSave = new AdminBiblioItemSave();

            itemSave.slug = slug;
            itemSave.titulo = title;
            itemSave.conteudo = content;
            itemSave.tipoItemBiblioteca = categoriaId;
            itemSave.status = _status;
            itemSave.publico = isPublic === 'true';
            itemSave.tags = tags;
            itemSave.autores = 'author';
            itemSave.linkYoutube = [link];
            itemSave.titleTag = titleTag;
            itemSave.metaDescriptionTag = metaDescriptionTag;

            if (itemSave.publico && !categoriaExistente()) {
                progress.setError('Categoria inativa');
                return;
            }

            let data, status: number;

            const responseUnicidade = await service.validarUnicidadeSlug(itemData?.id ?? null, itemSave.slug);
            let slugValido: boolean = false;
            if (responseUnicidade.status === 200 && responseUnicidade.data === true) {
                slugValido = true;
            }

            if (!slugValido) {
                progress.setError('URL Slug já cadastrado para outro conteúdo');
                return;
            }

            if (!itemData) {
                const response = await service.criarItem(itemSave);
                data = response.data;
                status = response.status;
            } else {
                const response = await service.atualizarItem2(itemData.id, itemSave, tags);
                data = response.data;
                status = response.status;
            }

            if (status !== 200) {
                throw new Error('Erro ao salvar item');
            }

            progress.setCreated();

            // Upload da imagem se existir
            if (imageCover) {
                progress.setUploading();

                try {
                    await submitAttach(data.id, imageCover, AdminBiblioAnexoType.CAPA);
                    progress.setSuccess('✓ Imagem salva com sucesso');
                } catch (imageError: any) {
                    const errorMessage = imageError.message || 'Erro ao fazer upload da imagem';
                    progress.setError(errorMessage);
                    return;
                }
            } else {
                progress.setSuccess();
            }

            // Aguarda um pouco antes de fechar para o usuário ver a mensagem de sucesso
            setTimeout(() => {
                onSaved();
                progress.reset();
            }, 1500);
        } catch (error: any) {
            const errorMessage = error.message || 'Erro ao processar a requisição';
            progress.setError(errorMessage);
        }
    };

    const fillData = async () => {
        if (itemData != null) {
            setSlug(itemData.slug);
            setId(itemData.id);
            setContent(itemData.content ?? '');
            setTitle(itemData.title);
            setIsPublic('false');
            setTitleTag(itemData.titleTag);
            setMetaDescriptionTag(itemData.metaDescriptionTag);

            if (itemData.linkSrc && itemData.linkSrc[0]) setLink(itemData.linkSrc[0] ?? '');

            const _imageCover = await fecthAttachImage(itemData.id, AdminBiblioAnexoType.CAPA);
            if (_imageCover) setPreImageCover(_imageCover);

            const tags = await fetchTagsItemId(itemData.id);
            setTags(tags);
        }
    };

    useEffect(() => {
        fillData();
    }, []);

    return (
        <Styled.Container>
            <Styled.Row>
                <Styled.FormContainer>
                    {isMobile() && (
                        <Styled.ImageContainer>
                            <AdminImageUploader
                                onSelect={(_, f) => setImageCover(f)}
                                size={'sm'}
                                title={'Imagem da capa'}
                                preImageB64={preimageCover}
                                recommendedDimension={{ w: 200, h: 130 }}
                            />
                        </Styled.ImageContainer>
                    )}

                    <br />
                    <Styled.Label>Url Slug</Styled.Label>
                    <Styled.Input
                        value={slug}
                        onChange={(e: any) => setSlug(StringHelp.alnum(StringHelp.toSize(e.target.value.toLowerCase().trim(), 150)))}
                    />
                    <br />
                    <Styled.Label>Título</Styled.Label>
                    <Styled.Input value={title} onChange={(e: any) => setTitle(StringHelp.toSize(e.target.value, 97))} />
                    <br />
                    <Styled.Label>Descrição</Styled.Label>
                    <ReactQuill
                        theme="snow"
                        modules={editorConfig.modules}
                        formats={editorConfig.formats}
                        value={content}
                        onChange={val => {
                            setContent(val);
                        }}
                    />
                    <br />

                    <Styled.Label>Link do vídeo no Youtube</Styled.Label>

                    <Styled.Row style={{ alignItems: 'center' }}>
                        <Styled.Input value={link} onChange={(e: any) => setLink(e.target.value)} />
                        <Styled.CheckIcon src={checkIcon} show={validLink(link)} />
                    </Styled.Row>

                    <br />
                    <AdminTagSelector tags={tags} onSelected={(newTags: string[]) => setTags(newTags)} />

                    <br />
                    <Styled.Label>Título da Página</Styled.Label>
                    <Styled.Input value={titleTag} onChange={(e: any) => setTitleTag(StringHelp.toSize(e.target.value, 150))} />

                    <br />
                    <Styled.Label>Meta Description</Styled.Label>
                    <Styled.Input
                        value={metaDescriptionTag}
                        onChange={(e: any) => setMetaDescriptionTag(StringHelp.toSize(e.target.value, 500))}
                    />

                    <br />
                    <Styled.Label>Tipo de visualização</Styled.Label>

                    <Styled.CkeckContainer style={{ paddingTop: '12px' }}>
                        <RadioGroup name={'isPublic'} onChange={setIsPublic} selectedValue={'false'}>
                            <label htmlFor="isPublic">
                                <Radio id="isPublic" value={'true'} />
                                <Styled.TextCheckBox>Público</Styled.TextCheckBox>
                            </label>

                            <label htmlFor="isPrivate">
                                <Radio id="isPrivate" value={'false'} />
                                <Styled.TextCheckBox>Privado</Styled.TextCheckBox>
                            </label>
                        </RadioGroup>
                    </Styled.CkeckContainer>
                    <br />
                    <ItemBibliotecaRelatedContentListagem itemData={itemData} />
                    <br />
                    <Styled.BottomRight>
                        <progress.ProgressComponent />
                        {isSaveRascunho(itemData) && (
                            <OutlineButton disabled={!isValidInputs() || progress.config.isSubmitting} onClick={() => submit('RASCUNHO')}>
                                {progress.config.isSubmitting ? 'Salvando...' : 'Salvar Rascunho'}
                            </OutlineButton>
                        )}
                        <GradientButton
                            disabled={!isValidInputs() || progress.config.isSubmitting}
                            onClick={() => submit('PUBLICADO')}
                            style={{ marginLeft: '10px', marginTop: '10px' }}
                        >
                            {progress.config.isSubmitting ? 'Salvando...' : 'Publicar'}
                        </GradientButton>
                    </Styled.BottomRight>
                </Styled.FormContainer>

                {!isMobile() && (
                    <Styled.ImageContainer>
                        <AdminImageUploader
                            onSelect={(_, f) => setImageCover(f)}
                            size={'sm'}
                            title={'Imagem da capa'}
                            preImageB64={preimageCover}
                            recommendedDimension={{ w: 200, h: 130 }}
                        />
                    </Styled.ImageContainer>
                )}
            </Styled.Row>
        </Styled.Container>
    );
};

export default AdminBiblioNewVideo;
